import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { getISOWeekNumber } from '../utils/date-range';

interface Props {
  onChange: (weekStart: number, weekEnd: number) => void;
  onInitialized?: (weekStart: number, weekEnd: number) => void;
  /** how many weeks back to default (default 10) */
  weeksBack?: number;
  /** selected year from parent */
  year: number;
  /** className for the wrapper div — use to control sizing from parent */
  className?: string;
}

const paperStyle: React.CSSProperties = { maxHeight: 40 * 6.5, overflowY: 'auto' };
const SELECT_MENU_PROPS = {
  MenuProps: {
    disableScrollLock: true,
    getContentAnchorEl: null,
    PaperProps: { style: paperStyle }
  }
};

const WeekRangeSelector: React.FC<Props> = ({ onChange, onInitialized, weeksBack = 10, year, className }) => {
  const { t } = useTranslation();
  const hasInitializedRef = React.useRef(false);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getISOWeekNumber(now);
  const defaultStart = Math.max(1, currentWeek - weeksBack);

  const [startWeek, setStartWeek] = useState(defaultStart);
  const [endWeek, setEndWeek] = useState(currentWeek);

  const weeks = Array.from({ length: 53 }, (_, i) => i + 1);
  const isFutureWeek = (w: number) => year >= currentYear && w > currentWeek;

  // Notify parent of initial values
  useEffect(() => {
    if (!hasInitializedRef.current && onInitialized) {
      hasInitializedRef.current = true;
      onInitialized(startWeek, endWeek);
    }
  }, []);

  // Notify parent when week range changes
  useEffect(() => {
    onChange(startWeek, endWeek);
  }, [startWeek, endWeek]);

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel id="from-week-label">{t('f-com-week-from')}</InputLabel>
        <Select
          {...SELECT_MENU_PROPS}
          labelId="from-week-label"
          value={startWeek}
          onChange={(e) => {
            const newStart = Number(e.target.value);
            setStartWeek(newStart);
            if (newStart > endWeek) {
              setEndWeek(newStart);
            }
          }}
          label={t('f-com-week-from')}
        >
          {weeks.map((w) => (
            <MenuItem key={w} value={w} disabled={isFutureWeek(w)}>
              {`${t('f-com-week')} ${w}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel id="to-week-label">{t('f-com-week-to')}</InputLabel>
        <Select
          {...SELECT_MENU_PROPS}
          labelId="to-week-label"
          value={endWeek}
          onChange={(e) => {
            const newEnd = Number(e.target.value);
            setEndWeek(newEnd);
            if (newEnd < startWeek) {
              setStartWeek(newEnd);
            }
          }}
          label={t('f-com-week-to')}
        >
          {weeks.map((w) => (
            <MenuItem key={w} value={w} disabled={isFutureWeek(w)}>
              {`${t('f-com-week')} ${w}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

export default WeekRangeSelector;
